import { createBrowserHistory } from 'history';
import * as React from 'react';
import * as ReactGA from 'react-ga';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Router } from 'react-router';
import { gaTrackerKey } from './api';
import { ErrorWrapper } from './containers';
import { useSetMobileDevice } from './hooks';
import { selectCurrentLanguage, selectMobileDeviceState, selectUserInfo, selectUserLoggedIn } from './modules';
import { appUrls } from './api-new/urls';
import { notification } from 'antd';
import { selectCurrentTicker } from './modules/public/tickers/selector';
import { fetchOrderBookRequest } from './modules/public/stock-order-book/actions';
import { fetchUserAssetsRequest } from './modules/user/assets/actions';
import { fetchMatchDataRequest, fetchOpenOrderRequest } from './modules/user/stock-order/actions';
import { fetchNotificationRequest, pushListNotification } from './modules/user/notifications/actions';
import { Widget } from 'react-chat-widget';
import 'react-chat-widget/lib/styles.css';
import { selectAIAnalysisData } from './modules/user/ai_analysis/selector';
import { fetchOrderHistoryRequest, fetchOrderMatchHistoryRequest } from './modules/user/stock-order-history';
import { TourManager } from './components/TourManager';
import { Route, Switch } from 'react-router-dom';
import { AppDownloadRedirectScreen } from './screens';

const gaKey = gaTrackerKey();
const browserHistory = createBrowserHistory();

if (gaKey) {
    ReactGA.initialize(gaKey);
    browserHistory.listen((location) => {
        ReactGA.set({ page: location.pathname });
        ReactGA.pageview(location.pathname);
    });
}

/* Desktop components */
const AlertsContainer = React.lazy(() => import('./containers/Alerts').then(({ Alerts }) => ({ default: Alerts })));
const CustomizationContainer = React.lazy(() =>
    import('./containers/Customization').then(({ Customization }) => ({ default: Customization })),
);
const FooterContainer = React.lazy(() => import('./containers/Footer').then(({ Footer }) => ({ default: Footer })));
const HeaderContainer = React.lazy(() => import('./containers/Header').then(({ Header }) => ({ default: Header })));
const SidebarContainer = React.lazy(() => import('./containers/Sidebar').then(({ Sidebar }) => ({ default: Sidebar })));
const LayoutContainer = React.lazy(() => import('./routes').then(({ Layout }) => ({ default: Layout })));
const ChartScreen = React.lazy(() => import('./screens/ChartScreen').then(({ ChartScreen }) => ({ default: ChartScreen })));

const getTranslations = (lang: string, isMobileDevice: boolean) => {
    if (isMobileDevice) {
        return {
            // ...languageMap[lang],
            // ...mobileTranslations[lang],
        };
    }

    return;
};

const RenderDeviceContainers = () => {
    const [isShowChat, setIsShowChat] = React.useState(false);
    const analysisData = useSelector(selectAIAnalysisData);
    React.useEffect(() => {
        setIsShowChat(!!analysisData.BasicInformation.CeilingPrice);
    }, [analysisData]);
    return (
        <React.Fragment>
            <HeaderContainer />
            <SidebarContainer />
            <CustomizationContainer />
            <AlertsContainer />
            <LayoutContainer />
            <FooterContainer />
            {/* {isShowChat && <Widget title="Chat with us" subtitle="We're here to help!" />} */}
        </React.Fragment>
    );
};
export let notificationsocket: any;
const App = () => {
    React.useEffect(() => {
        return () => {
            sessionStorage.clear();
        }
    }, []);
    useSetMobileDevice();
    const lang = useSelector(selectCurrentLanguage);
    const isMobileDevice = useSelector(selectMobileDeviceState);
    const isLogin = useSelector(selectUserLoggedIn);
    const userInfo = useSelector(selectUserInfo);
    const dispatch = useDispatch();
    const currentTicker = useSelector(selectCurrentTicker);
    React.useEffect(() => {
        if (isLogin) {
            dispatch(fetchNotificationRequest());
            notificationsocket = new WebSocket(`${appUrls.socketOrderURL}?token=${localStorage.getItem('csrfToken')}`);
            console.log('WebSocket notification connected');

            notificationsocket.onopen = () => {
                console.log('WebSocket notification is open.');
            };

            notificationsocket.onmessage = (event: any) => {
                const data = JSON.parse(event.data);
                dispatch(pushListNotification(data));
                notification.info({ message: 'Order Match', description: data.message, duration: 3 });
                dispatch(fetchOrderBookRequest(currentTicker?.ticker));
                dispatch(fetchOpenOrderRequest(currentTicker?.ticker));
                dispatch(fetchOpenOrderRequest(currentTicker?.ticker));
                dispatch(fetchMatchDataRequest(currentTicker?.ticker));
                dispatch(fetchUserAssetsRequest());
                dispatch(fetchOrderHistoryRequest());
                dispatch(fetchOrderMatchHistoryRequest());
            };

            notificationsocket.onerror = (error: any) => {
                console.error('WebSocket Error:', error);
            };
        }
        if ((window as any).umami && userInfo.email && isLogin) {
            (window as any).umami.identify(userInfo.email);
        }
        return () => {
            if (notificationsocket) {
                // console.log('close');
                notificationsocket.close();
            }
        };
    }, [isLogin]);
    return (
        <IntlProvider locale={lang} messages={getTranslations(lang, isMobileDevice)} key={lang}>
            <Router history={browserHistory}>
                <ErrorWrapper>
                    <React.Suspense fallback={null}>
                        <Switch>
                            <Route path="/chart" component={ChartScreen} />
                            <Route path="/">
                                <RenderDeviceContainers />
                            </Route>
                        </Switch>
                        <TourManager />
                    </React.Suspense>
                </ErrorWrapper>
            </Router>
        </IntlProvider>
    );
};

export default App;
